import React from "react";
import { Box, Typography } from "@mui/material";
import DashboardCard from "./DashboardCard";
import { DASH } from "../dashboardTheme";

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return "—";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export default function FieldReportsOverview({ stats, eventStats, loading }) {
  const total = stats?.totalReports ?? 0;
  const avgMinutes = stats?.averageVisitMinutes ?? 0;
  const totalMinutes = stats?.totalVisitMinutes ?? 0;

  const completedThisMonth = (eventStats?.byStatus || []).reduce((sum, s) => {
    if (s._id === "COMPLETED") return sum + (s.count ?? 0);
    return sum;
  }, 0);

  const coverage =
    completedThisMonth > 0 ? Math.min(100, Math.round((total / completedThisMonth) * 100)) : null;

  const metrics = [
    { label: "Reportes", value: total.toLocaleString("es-MX") },
    { label: "Duración prom.", value: formatDuration(avgMinutes) },
    { label: "Tiempo total", value: formatDuration(totalMinutes) },
  ];

  return (
    <DashboardCard
      title="Reportes de campo"
      subtitle={loading ? "Cargando..." : "Documentación del mes en curso"}
      sx={{ height: "100%" }}
    >
      {loading ? (
        <Box className="dash-skeleton" sx={{ height: 140 }} />
      ) : total === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: DASH.text, mb: 0.5 }}>
            Sin reportes este mes
          </Typography>
          <Typography sx={{ fontSize: "12px", color: DASH.textMuted, maxWidth: 280, mx: "auto" }}>
            Los reportes generados desde el calendario aparecerán aquí
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 0,
              border: `1px solid ${DASH.borderLight}`,
              borderRadius: DASH.radiusSm,
              overflow: "hidden",
              mb: 2,
            }}
          >
            {metrics.map(({ label, value }, idx) => (
              <Box
                key={label}
                sx={{
                  p: 1.75,
                  textAlign: "center",
                  borderRight: idx < metrics.length - 1 ? `1px solid ${DASH.borderLight}` : "none",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: DASH.fontMono,
                    fontSize: "1.25rem",
                    fontWeight: 500,
                    color: DASH.text,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {value}
                </Typography>
                <Typography sx={{ fontSize: "10px", color: DASH.textMuted, mt: 0.5, fontWeight: 500 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {coverage != null && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 500, color: DASH.textMuted }}>
                  Cobertura documental
                </Typography>
                <Typography
                  sx={{
                    fontFamily: DASH.fontMono,
                    fontSize: "12px",
                    fontWeight: 500,
                    color: DASH.text,
                  }}
                >
                  {coverage}%
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 3,
                  borderRadius: 999,
                  bgcolor: DASH.borderLight,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${coverage}%`,
                    bgcolor: DASH.text,
                    borderRadius: 999,
                    transition: "width 0.5s ease",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "11px", color: DASH.textMuted, mt: 0.75 }}>
                {total} reportes de {completedThisMonth} servicios completados
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </DashboardCard>
  );
}
